import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, API_BASE, getBearer } from "../../lib/api";
import { extractError } from "../../lib/format";
import { Loader2, Download, ExternalLink, RefreshCw } from "lucide-react";

function bandLabel(id) {
  if (!id) return "\u2014";
  return "Ages " + id.replace("age_", "").replace("-", "\u2013");
}
function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function AdminCertificates() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [busySlug, setBusySlug] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/certificates?limit=500");
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: extractError(e, "Could not load certificates.") });
    }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (slug) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/certificates/${encodeURIComponent(slug)}/download`, {
        headers: { Authorization: `Bearer ${getBearer()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e, "Could not download PDF."));
    }
  };

  const reissue = async (slug) => {
    setBusySlug(slug);
    try {
      await api.post(`/admin/certificates/reissue/${encodeURIComponent(slug)}`);
      toast.success("PDF regenerated.");
      await load();
    } catch (e) {
      toast.error(extractError(e, "Reissue failed."));
    } finally {
      setBusySlug(null);
    }
  };

  return (
    <div className="ftg-adm__stack" data-testid="admin-certs-root">
      {state.loading && <div className="ftg-adm__empty" data-testid="admin-certs-loading"><Loader2 className="ftg-spin" size={16} /> Loading…</div>}
      {state.error && <div className="ftg-adm__empty">{state.error}</div>}
      {state.data && (
        <div className="ftg-adm__table-wrap">
          <table className="ftg-adm__table" data-testid="admin-certs-table">
            <thead>
              <tr>
                <th>Slug</th><th>Child</th><th>Type</th><th>Band</th><th>Term</th><th>Grade</th><th>Issued</th><th></th>
              </tr>
            </thead>
            <tbody>
              {state.data.items.map((c) => (
                <tr key={c.id} data-testid={`admin-cert-row-${c.cert_id_slug}`}>
                  <td className="ftg-adm__mono">{c.cert_id_slug}</td>
                  <td>{c.child_name || "\u2014"}</td>
                  <td>{c.cert_type === "level_2" ? "Level 2" : "Level 1"}</td>
                  <td>{bandLabel(c.age_band)}</td>
                  <td>{c.term ?? "\u2014"}</td>
                  <td><span className={`ftg-adm__pill is-grade-${c.grade || "none"}`}>{c.grade || "—"}</span></td>
                  <td className="ftg-adm__nowrap">{formatDate(c.issued_at)}</td>
                  <td className="ftg-adm__actions-cell">
                    <button type="button" className="ftg-btn ftg-btn--ghost ftg-btn--sm" onClick={() => downloadPdf(c.cert_id_slug)} data-testid={`admin-cert-download-${c.cert_id_slug}`}>
                      <Download size={12} /> PDF
                    </button>
                    <Link to={`/verify/${encodeURIComponent(c.cert_id_slug)}`} target="_blank" rel="noopener noreferrer" className="ftg-btn ftg-btn--ghost ftg-btn--sm">
                      <ExternalLink size={12} /> Verify
                    </Link>
                    <button type="button" className="ftg-btn ftg-btn--ghost ftg-btn--sm" onClick={() => reissue(c.cert_id_slug)} disabled={busySlug === c.cert_id_slug} data-testid={`admin-cert-reissue-${c.cert_id_slug}`}>
                      {busySlug === c.cert_id_slug ? <Loader2 size={12} className="ftg-spin" /> : <RefreshCw size={12} />} Reissue
                    </button>
                  </td>
                </tr>
              ))}
              {!state.data.items.length && <tr><td colSpan={8} className="ftg-adm__muted">No certificates issued yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
